import { useMemo, useState } from "react";
import { FolderOpen, Plus, MagnifyingGlass, ArrowRight, X, Files, LinkSimple, Clock } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { CATEGORIES, SUBJECTS, CategoryPill, SubjectPill, categoryColor, subjectColor } from "../lib/tags";

function Chip({ active, onClick, label, color, outline, testid }) {
  const style = active
    ? (outline ? { borderColor: color, color, background: "transparent" } : { background: color || "hsl(var(--primary))", color: "#fff", borderColor: color || "hsl(var(--primary))" })
    : { borderColor: "hsl(var(--border))", color: "hsl(var(--muted-foreground))", background: "transparent" };
  return (
    <button onClick={onClick} data-testid={testid}
      className="text-xs px-2.5 py-1 rounded-full border font-medium transition-colors"
      style={{ ...style, borderWidth: 1.5 }}>
      {label}
    </button>
  );
}

function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "ora";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min fa`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h fa`;
  const days = Math.floor(h / 24);
  if (days === 1) return "ieri";
  if (days < 30) return `${days} g fa`;
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
}

export function MyJobsDialog({ open, onClose, jobs, currentJobId, onSelect, onNew }) {
  const [cat, setCat] = useState("");
  const [sub, setSub] = useState("");
  const [q, setQ] = useState("");

  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (jobs || []).filter((j) =>
      (!cat || j.category === cat) &&
      (!sub || j.subject === sub) &&
      (!s || (j.name || "").toLowerCase().includes(s)));
  }, [jobs, cat, sub, q]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-popover border-border block w-[95vw] max-w-3xl p-0 overflow-hidden [&>button]:hidden" data-testid="my-jobs-dialog">
        <DialogTitle className="sr-only">I miei lavori</DialogTitle>
        <DialogDescription className="sr-only">Elenco completo dei lavori personali con filtri per categoria e soggetto.</DialogDescription>

        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-5 py-4 bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(var(--accent))] text-white">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center shrink-0"><FolderOpen size={20} weight="fill" /></div>
            <div className="min-w-0">
              <div className="font-head font-extrabold text-lg leading-none">I miei lavori</div>
              <div className="text-xs text-white/80 mt-1">{(jobs || []).length} lavori · organizzali per categoria e soggetto</div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button size="sm" onClick={onNew} data-testid="my-jobs-new-btn" className="gap-2 bg-white text-[hsl(var(--primary))] hover:bg-white/90">
              <Plus size={16} weight="bold" /> Nuovo
            </Button>
            <button onClick={onClose} data-testid="my-jobs-close-btn" className="text-white/80 hover:text-white p-1"><X size={20} /></button>
          </div>
        </div>

        {/* Filters */}
        <div className="px-5 pt-4 space-y-2 border-b border-border pb-4">
          <div className="relative">
            <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome…" data-testid="my-jobs-search" className="bg-muted pl-9 h-9" />
          </div>
          <div className="flex flex-wrap gap-1.5 items-center">
            <span className="text-[11px] font-semibold text-muted-foreground mr-1">Categoria</span>
            <Chip active={!cat} onClick={() => setCat("")} label="Tutte" />
            {CATEGORIES.map((c) => (
              <Chip key={c} active={cat === c} onClick={() => setCat(cat === c ? "" : c)} label={c} color={categoryColor(c)} testid={`myjobs-cat-${c}`} />
            ))}
          </div>
          <div className="flex flex-wrap gap-1.5 items-center">
            <span className="text-[11px] font-semibold text-muted-foreground mr-1">Soggetto</span>
            <Chip active={!sub} onClick={() => setSub("")} label="Tutti" />
            {SUBJECTS.map((s) => (
              <Chip key={s} active={sub === s} onClick={() => setSub(sub === s ? "" : s)} label={s} color={subjectColor(s)} outline testid={`myjobs-sub-${s}`} />
            ))}
          </div>
        </div>

        {/* Grid */}
        <div className="p-5 overflow-y-auto max-h-[48vh]">
          {list.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-10" data-testid="my-jobs-empty">
              {(jobs || []).length === 0 ? "Non hai ancora lavori. Creane uno con «Nuovo»." : "Nessun lavoro con questi filtri."}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="my-jobs-grid">
              {list.map((j) => {
                const active = j.id === currentJobId;
                return (
                  <button key={j.id} onClick={() => onSelect(j.id)} data-testid={`my-jobs-card-${j.id}`}
                    className={`group text-left rounded-xl border p-3 pl-4 transition-all hover:shadow-md hover:-translate-y-0.5 relative overflow-hidden ${active ? "border-primary ring-1 ring-primary bg-primary/5" : "border-border bg-card hover:border-primary/50"}`}>
                    <span className="absolute left-0 top-0 bottom-0 w-1.5" style={{ background: j.category ? categoryColor(j.category) : "transparent" }} />
                    <div className="flex items-start justify-between gap-2">
                      <span className="font-semibold text-sm leading-snug break-words">{j.name}</span>
                      <ArrowRight size={16} className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0 mt-0.5" />
                    </div>
                    {(j.category || j.subject) ? (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {j.category && <CategoryPill value={j.category} />}
                        {j.subject && <SubjectPill value={j.subject} />}
                      </div>
                    ) : (
                      <div className="text-[11px] text-muted-foreground mt-2 italic">senza categoria</div>
                    )}
                    <div className="flex items-center gap-3 mt-2.5 pt-2 border-t border-border/60 text-[11px] text-muted-foreground" data-testid={`my-jobs-meta-${j.id}`}>
                      <span className="inline-flex items-center gap-1" title="Fonti caricate"><Files size={13} /> {j.n_files ?? 0} fonti</span>
                      <span className="inline-flex items-center gap-1" title="Righe abbinate"><LinkSimple size={13} /> {j.n_matched ?? 0} abbinate</span>
                      <span className="inline-flex items-center gap-1 ml-auto" title="Ultima modifica"><Clock size={13} /> {timeAgo(j.updated_at)}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
